import {
    deleteSession,
    expireSession,
    getIsAuthenticatedOnServer,
} from "@insite/client-framework/Services/SessionService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentPage, getRequiresAuthorization } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";

const activityThrottleMs = 30_000;
const activityEvents = ["mousemove", "mousedown", "keydown", "touchstart", "scroll"] as const;
const localStorageKey = "isc_lastActivity";
const persistentSessionCheckIntervalMs = 60_000;

const SessionTimeoutWatcher = () => {
    const isAuthenticated = useSelector((state: ApplicationState) => state.context.session.isAuthenticated);
    const isGuest = useSelector((state: ApplicationState) => state.context.session.isGuest);
    const rememberMe = useSelector((state: ApplicationState) => state.context.session.rememberMe);
    const keepMeSignedIn = useSelector((state: ApplicationState) => state.context.session.keepMeSignedIn);
    const siteTimeoutMinutes = useSelector(
        (state: ApplicationState) => getSettingsCollection(state).accountSettings.siteTimeoutMinutes,
    );
    const currentPageRequiresAuth = useSelector((state: ApplicationState) =>
        getRequiresAuthorization(state, getCurrentPage(state).id),
    );
    const signInPageUrl = useSelector((state: ApplicationState) => getPageLinkByPageType(state, "SignInPage")?.url);
    const rememberMeMode = useSelector(
        (state: ApplicationState) => getSettingsCollection(state).accountSettings.rememberMe,
    );

    const isActive = isAuthenticated && !isGuest;

    const siteTimeoutMinutesRef = useRef(siteTimeoutMinutes);
    siteTimeoutMinutesRef.current = siteTimeoutMinutes;
    const currentPageRequiresAuthRef = useRef(currentPageRequiresAuth);
    currentPageRequiresAuthRef.current = currentPageRequiresAuth;
    const signInPageUrlRef = useRef(signInPageUrl);
    signInPageUrlRef.current = signInPageUrl;
    const rememberMeModeRef = useRef(rememberMeMode);
    rememberMeModeRef.current = rememberMeMode;

    useEffect(() => {
        if (!isActive || rememberMe || keepMeSignedIn) {
            return;
        }

        let timeoutId: ReturnType<typeof setTimeout> | undefined;
        let lastActivityReset = 0;

        const resetTimer = () => {
            if (timeoutId !== undefined) {
                clearTimeout(timeoutId);
            }
            const timeoutMs = siteTimeoutMinutesRef.current * 60_000;
            if (timeoutMs > 0) {
                timeoutId = setTimeout(handleTimeout, timeoutMs);
            }
        };

        const handleActivity = () => {
            const now = Date.now();
            if (now - lastActivityReset < activityThrottleMs) {
                return;
            }
            lastActivityReset = now;
            try {
                localStorage.setItem(localStorageKey, now.toString());
            } catch {
                // localStorage unavailable (private browsing, full storage, etc.)
            }
            resetTimer();
        };

        const handleTimeout = async () => {
            timeoutId = undefined;

            const timeoutMs = siteTimeoutMinutesRef.current * 60_000;
            try {
                const raw = localStorage.getItem(localStorageKey);
                if (raw) {
                    const remainingMs = Number(raw) + timeoutMs - Date.now();
                    if (remainingMs > 0) {
                        timeoutId = setTimeout(handleTimeout, remainingMs);
                        return;
                    }
                }
            } catch {
                // localStorage unavailable — proceed with logout
            }

            if (timeoutId !== undefined) {
                clearTimeout(timeoutId);
                timeoutId = undefined;
            }
            for (const event of activityEvents) {
                window.removeEventListener(event, handleActivity);
            }

            try {
                localStorage.removeItem(localStorageKey);
            } catch {
                // localStorage unavailable
            }

            if (rememberMeModeRef.current) {
                await expireSession();
                window.location.reload();
                return;
            }

            await deleteSession();

            if (currentPageRequiresAuthRef.current && signInPageUrlRef.current) {
                window.location.href = signInPageUrlRef.current;
            } else {
                window.location.reload();
            }
        };

        resetTimer();
        for (const event of activityEvents) {
            window.addEventListener(event, handleActivity, { passive: true });
        }

        return () => {
            if (timeoutId !== undefined) {
                clearTimeout(timeoutId);
            }
            for (const event of activityEvents) {
                window.removeEventListener(event, handleActivity);
            }
        };
    }, [isActive, rememberMe, keepMeSignedIn]);

    // keepMeSignedIn reflects a persistent auth cookie, which both the Keep Me Signed In and
    // Remember Me sign-in strategies issue. These sessions are skipped by the inactivity watcher
    // above and have no client-readable expiry (the auth cookie is httpOnly), so poll the server to
    // detect when the session has ended (e.g. the cookie reached Days To Retain User) and reload.
    // Reloading lets the navigation filter redirect to sign-in when the current page requires it.
    // (The rememberMe flag is a separate post-expiry "remembered-only" state, not gated here.)
    useEffect(() => {
        if (!isActive || !keepMeSignedIn) {
            return;
        }

        let cancelled = false;
        let inFlight = false;

        const checkAuthentication = async () => {
            if (cancelled || inFlight || document.hidden) {
                return;
            }
            inFlight = true;
            try {
                const isAuthenticatedOnServer = await getIsAuthenticatedOnServer();
                if (!cancelled && !isAuthenticatedOnServer) {
                    if (currentPageRequiresAuthRef.current && signInPageUrlRef.current) {
                        window.location.href = signInPageUrlRef.current;
                    } else {
                        window.location.reload();
                    }
                }
            } catch {
                // Transient failure (e.g. network) — re-check on the next interval.
            } finally {
                inFlight = false;
            }
        };

        const intervalId = setInterval(checkAuthentication, persistentSessionCheckIntervalMs);
        const handleVisibilityChange = () => {
            if (!document.hidden) {
                checkAuthentication();
            }
        };
        document.addEventListener("visibilitychange", handleVisibilityChange);

        return () => {
            cancelled = true;
            clearInterval(intervalId);
            document.removeEventListener("visibilitychange", handleVisibilityChange);
        };
    }, [isActive, keepMeSignedIn]);

    return null;
};

export default SessionTimeoutWatcher;
